import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { generateCanonicalUrl, generateHreflangTags, ensureHttpsUrl } from '../utils/seoUtils';
import { isCanonicalRoute, LanguageCode, safeLanguageCode } from '../utils/languageUtils';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  image?: string;
  noindex?: boolean;
  structuredData?: object;
  hreflangEnabled?: boolean;
}

const SEO = ({
  title,
  description,
  canonical,
  image = '/og-image.png',
  noindex = false,
  structuredData,
  hreflangEnabled = true
}: SEOProps) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const currentLang = safeLanguageCode(i18n.language);
  
  // Default values
  const defaultTitle = currentLang === 'en' 
    ? 'AlertaLegal - Legal Publication Alerts for Costa Rica'
    : 'AlertaLegal - Alertas de Publicaciones Legales de Costa Rica';
    
  const defaultDescription = currentLang === 'en'
    ? 'Get email notifications when your name or national ID appears in official legal publications in Costa Rica. Monitor La Gaceta and other official documents automatically.'
    : 'Recibe notificaciones por correo cuando tu nombre o cédula aparezca en publicaciones legales oficiales de Costa Rica. Monitorea La Gaceta y otros documentos oficiales automáticamente.';

  const siteTitle = title ? `${title} | AlertaLegal` : defaultTitle;
  const siteDescription = description || defaultDescription;
  // Validate and ensure proper site URL configuration
  const validateSiteUrl = (url: string): string => {
    try {
      const validated = ensureHttpsUrl(url);
      
      // In production, ensure we're not using localhost
      if (import.meta.env.PROD) {
        const parsedUrl = new URL(validated);
        if (parsedUrl.hostname === 'localhost' || parsedUrl.hostname === '127.0.0.1') {
          console.error('Production environment using localhost URL. Using fallback.');
          return 'https://alertalegal.co';
        }
        
        // Ensure production uses HTTPS
        if (parsedUrl.protocol !== 'https:') {
          console.error('Production environment must use HTTPS. Using fallback.');
          return 'https://alertalegal.co';
        }
      }
      
      return validated;
    } catch (error) {
      console.error('Invalid VITE_SITE_URL configuration:', error);
      return 'https://alertalegal.co';
    }
  };

  const siteUrl = validateSiteUrl(import.meta.env.VITE_SITE_URL || 'https://alertalegal.co');
  
  // Development warning for fallback usage
  if (import.meta.env.DEV && !import.meta.env.VITE_SITE_URL) {
    console.warn('VITE_SITE_URL not configured. Using fallback URL for development.');
  }
  const fullImageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
  
  // Generate proper canonical URL using seoUtils for multilingual structure
  const canonicalUrl = canonical || generateCanonicalUrl(
    location.pathname,
    currentLang,
    siteUrl,
    isCanonicalRoute(location.pathname)
  );

  // Memoize hreflang tags to avoid regenerating on every render
  const hreflangElements = useMemo(() => {
    if (!hreflangEnabled) return null;
    
    const hreflangTags = generateHreflangTags(location.pathname, siteUrl, currentLang);
    return Object.entries(hreflangTags).map(([lang, url]) => (
      <link 
        key={`hreflang-${lang}`}
        rel="alternate" 
        hrefLang={lang} 
        href={url} 
      />
    ));
  }, [hreflangEnabled, location.pathname, siteUrl, currentLang]);

  return (
    <>
      {/* Basic Meta Tags */}
      <title>{siteTitle}</title>
      <meta name="description" content={siteDescription} />
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow'} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Language and Locale */}
      <meta httpEquiv="Content-Language" content={currentLang === 'en' ? 'en-US' : 'es-CR'} />
      
      {/* Hreflang tags for multilingual SEO with self-referencing */}
      {hreflangElements}
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={siteDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={currentLang === 'en' ? 'en_US' : 'es_CR'} />
      <meta property="og:site_name" content="AlertaLegal" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={siteDescription} />
      <meta name="twitter:image" content={fullImageUrl} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#1a365d" />
      <meta name="msapplication-TileColor" content="#1a365d" />
      
      {/* Costa Rica specific keywords for better local SEO */}
      {currentLang === 'es' && (
        <meta name="keywords" content="alertas legales, publicaciones oficiales, La Gaceta, Costa Rica, notificaciones legales, cédula, documentos oficiales, monitoreo legal" />
      )}
      {currentLang === 'en' && (
        <meta name="keywords" content="legal alerts, official publications, La Gaceta, Costa Rica, legal notifications, national ID, official documents, legal monitoring" />
      )}
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </>
  );
};

export default SEO;